/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useLazyQuery, useQuery } from "@apollo/client";
import { queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import useAddEditNews from "hooks/useAddEditNews";
import useAddNews from "hooks/useAddNews";
import useImageSelector from "hooks/useImageSelector";
import useNewsCritics from "hooks/useNewsCritics";
import useTMDBFindByImdbId from "hooks/useTMDBFindById";
import useTMDBSearch from "hooks/useTMDBSearch";
import useUser from "hooks/useUser";
import { useCallback, useEffect, useMemo, useState } from "react";
const { content: { GET_CONTENT_AUTHORS }, homeVideos: { SEARCH_HOME_VIDEOS }, news: { SEARCH_OUTLETS }, vendors: { SEARCH_LABELS_BY_URL } } = queries;
const useNewsAddActions = () => {
    const { IMDbId, isAddNewsEnabled, isImdbIdValid, loading, onAddNews, name } = useAddNews();
    const { onClearAll } = useAddEditNews();
    const { setAuthorId, setHomeVideoId, setIMDbId, setName, setReleaseDate, setTmdbSelectedImdbId, setTmdbSelectedId, setSelectedSingleSource, state: { news, news: { homeVideoId, isAddHomeVideo, releaseDate }, singleSource, tmdbSelectedImdbId, tmdbSelectedId } } = useNewsCritics();
    // Effective IMDb ID: TMDB selection supersedes critic review selection
    const effectiveImdbId = useMemo(() => tmdbSelectedImdbId ?? news.IMDbId, [news.IMDbId, tmdbSelectedImdbId]);
    const { isAdmin, isEditor } = useUser();
    const imageSelector = useImageSelector({ serviceType: "news" });
    const screenshotSelector = useImageSelector({
        mediaType: "screenshots",
        serviceType: "news"
    });
    // TMDB search state
    const [tmdbSearchQuery, setTmdbSearchQuery] = useState("");
    const { loading: tmdbLoading, data: tmdbData } = useTMDBSearch({
        query: tmdbSearchQuery,
        type: "both"
    });
    // TMDB find by IMDb ID (auto-triggered when critic review sets IMDbId)
    const { data: tmdbFindByData } = useTMDBFindByImdbId({
        imdbId: news.IMDbId
    });
    // Compute suggestions directly from data (no useEffect needed)
    const tmdbSuggestions = useMemo(() => {
        // Pre-filled result from IMDbID lookup (highest priority)
        const findByResults = tmdbFindByData?.map((item) => ({
            id: item.id.toString(),
            title: item.title,
            year: item.year,
            mediaType: item.mediaType,
            poster: item.poster,
            overview: item.overview,
            externalIds: item.externalIds
        })) ?? [];
        // User-typed search results
        const searchResults = tmdbData?.map((item) => ({
            id: item.id.toString(),
            title: item.title,
            year: item.year,
            mediaType: item.mediaType,
            poster: item.poster,
            overview: item.overview,
            externalIds: item.externalIds
        })) ?? [];
        // Combine: findBy result first, then search results
        return [...findByResults, ...searchResults];
    }, [tmdbData, tmdbFindByData]);
    // Handle TMDB search
    const handleTmdbSearch = useCallback((query) => {
        setTmdbSearchQuery(query);
    }, []);
    // Handle TMDB selection
    const handleTmdbSelect = useCallback((tmdbId) => {
        setTmdbSelectedId(tmdbId);
        const selectedItem = tmdbSuggestions.find((item) => item.id === tmdbId);
        if (selectedItem?.externalIds?.imdbId) {
            setTmdbSelectedImdbId(selectedItem.externalIds.imdbId);
        }
    }, [setTmdbSelectedId, setTmdbSelectedImdbId, tmdbSuggestions]);
    // Auto-select TMDB result when findTMDB returns exactly 1 item
    // and no TMDB selection exists yet (i.e., IMDb ID came from critic review)
    useEffect(() => {
        if (tmdbFindByData?.length === 1 && !tmdbSelectedId) {
            const [item] = tmdbFindByData;
            setTmdbSelectedId(item.id.toString());
            if (item.externalIds?.imdbId) {
                setTmdbSelectedImdbId(item.externalIds.imdbId);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tmdbFindByData]);
    /**
     * onImdbIdChange
     *
     * @description
     * Sets the IMDb ID for the news item.
     * @param {string} newIMDbId - The new IMDb ID to set.
     * @returns {void}
     *
     */
    const onImdbIdChange = useCallback((newIMDbId) => {
        setIMDbId(newIMDbId);
    }, [setIMDbId]);
    /**
     *  GET_CONTENT_AUTHORS
     *
     *  @description
     *  Fetches the list of content authors for news items.
     *  @returns {ContentAuthor[]} An array of content authors.
     *
     */
    const { data: getContentAuthorsData } = useQuery(GET_CONTENT_AUTHORS);
    /**
     * searchHomeVideos
     *
     * @description
     * Searches for home videos based on the provided input.
     * @returns {HomeVideoSearchItem[]} An array of home video search items.
     *
     */
    const [searchHomeVideos, { data: searchHomeVideosData, loading: searchHomeVideosLoading }] = useLazyQuery(SEARCH_HOME_VIDEOS);
    /**
     * searchLabels
     *
     * @description
     * Searches for labels based on the provided input.
     * @returns
     * {LabelSearchItem[]} An array of label search items.
     *
     */
    const [searchLabels, { data: searchLabelsData, loading: searchLabelsLoading }] = useLazyQuery(SEARCH_LABELS_BY_URL);
    /**
     * searchOutlets
     *
     * @description
     * Searches for news outlets based on the provided input.
     * @returns
     * {OutletsSearchItem[]} An array of news outlet search items.
     *
     */
    const [searchOutlets, { data: searchOutletsData, loading: searchOutletsLoading }] = useLazyQuery(SEARCH_OUTLETS);
    /**
     * onContentAuthorSelect
     *
     * @description
     * Sets the selected content author ID.
     *
     * @param authorId - The ID of the selected content author.
     *
     */
    const onContentAuthorSelect = useCallback((authorId) => {
        if (authorId) {
            setAuthorId(Number(authorId));
        }
    }, [setAuthorId]);
    /**
     * onNameChange
     *
     * @param newName
     * @description
     * Sets the name for the news item.
     * @returns {void}
     *
     */
    const onNameChange = useCallback((newName) => {
        setName(newName);
    }, [setName]);
    /**
     * onGetHomeVideoSuggestions
     *
     * @description
     * Fetches home video suggestions based on the input value.
     * @returns
     * {AutosuggestProps["onGetSuggestions"]} Function to fetch home video suggestions.
     *
     */
    const onGetHomeVideoSuggestions = useCallback(({ inputValue }) => {
        searchHomeVideos({
            variables: {
                searchHomeVideosInput: {
                    title: inputValue
                }
            }
        });
    }, [searchHomeVideos]);
    /**
     * onGetOutletsSuggestions
     *
     * @description
     * Fetches news outlet suggestions based on the input value.
     * @returns
     * {AutosuggestProps["onGetSuggestions"]} Function to fetch news outlet suggestions.
     *
     */
    const onGetOutletsSuggestions = useCallback(({ inputValue }) => {
        searchLabels({
            variables: {
                searchLabelsByUrlInput: {
                    url: inputValue
                }
            }
        });
        searchOutlets({
            variables: {
                searchOutletsInput: {
                    name: inputValue
                }
            }
        });
    }, [searchLabels, searchOutlets]);
    /**
     * searchHomeVideo
     *
     * @description
     * Configuration for searching home videos.
     * @returns
     * {AddNewsActionsProps["searchHomeVideo"]} Configuration object for searching home videos.
     *
     */
    const searchHomeVideo = useMemo(() => ({
        homeVideoTitles: searchHomeVideosData?.searchHomeVideos,
        isLoading: searchHomeVideosLoading,
        isSearchHomeVideo: !isAddHomeVideo,
        onGetHomeVideoSuggestions,
        onSelectHomeVideoId: setHomeVideoId,
        selectedHomeVideoId: homeVideoId
    }), [
        homeVideoId,
        isAddHomeVideo,
        onGetHomeVideoSuggestions,
        searchHomeVideosData?.searchHomeVideos,
        searchHomeVideosLoading,
        setHomeVideoId
    ]);
    /**
     *  onSetSelectedSingleSource
     *
     *  @description
     *  Sets the selected single source (label or outlet) based on the provided ID and URL.
     *  @param {string} id - The ID of the selected source.
     *  @param {string} url - The URL associated with the selected source.
     *  @returns {void}
     *
     */
    const onSetSelectedSingleSource = useCallback((id, url) => {
        const labelId = searchLabelsData?.searchLabelsByUrl.find((label) => label.id === id)?.id;
        const outletId = searchOutletsData?.searchOutlets.find((outlet) => outlet.id === id)?.id;
        if (labelId) {
            setSelectedSingleSource({ labelId, url });
        }
        else if (outletId) {
            setSelectedSingleSource({ outletId, url });
        }
    }, [
        searchLabelsData?.searchLabelsByUrl,
        searchOutletsData?.searchOutlets,
        setSelectedSingleSource
    ]);
    /**
     * searchNewsSource
     *
     * @description
     * Configuration for searching news sources.
     * @returns
     * {AddNewsActionsProps["searchNewsSource"]} Configuration object for searching news sources.
     *
     */
    const searchNewsSource = useMemo(() => ({
        isLoading: searchLabelsLoading || searchOutletsLoading,
        labels: [
            ...(searchLabelsData?.searchLabelsByUrl.map((item) => ({
                id: item.id,
                name: item.vendor.name
            })) || [])
        ],
        onGetSuggestions: onGetOutletsSuggestions,
        onSelectId: onSetSelectedSingleSource,
        outlets: [...(searchOutletsData?.searchOutlets || [])],
        selectedId: singleSource.labelId ?? singleSource.outletId
    }), [
        onGetOutletsSuggestions,
        onSetSelectedSingleSource,
        searchLabelsData?.searchLabelsByUrl,
        searchLabelsLoading,
        searchOutletsData?.searchOutlets,
        searchOutletsLoading,
        singleSource.labelId,
        singleSource.outletId
    ]);
    /**
     * IMDb
     *
     * @description
     * Configuration for IMDb ID input with TMDB search support.
     * @returns {AddNewsActionsProps["IMDb"]} Configuration object for IMDb ID input.
     *
     */
    const IMDb = useMemo(() => ({
        IMDbId: effectiveImdbId,
        hasSelectedIcon: Boolean(tmdbSelectedId),
        isImdbIdValid,
        onImdbIdChange,
        onTmdbSearch: handleTmdbSearch,
        onTmdbSelect: handleTmdbSelect,
        tmdbSelectedId,
        tmdbSearchQuery,
        tmdbSuggestions,
        tmdbLoading
    }), [
        effectiveImdbId,
        tmdbFindByData?.length,
        isImdbIdValid,
        onImdbIdChange,
        handleTmdbSearch,
        handleTmdbSelect,
        tmdbSelectedId,
        tmdbSearchQuery,
        tmdbSuggestions,
        tmdbLoading
    ]);
    /**
     * onSetReleaseDate
     *
     * @description
     * Sets the release date for the news item.
     * @param {string} releaseDate - The release date to set.
     * @returns {void}
     *
     */
    const onSetReleaseDate = useCallback((newReleaseDate) => {
        setReleaseDate(newReleaseDate);
    }, [setReleaseDate]);
    return {
        IMDb,
        contentAuthors: getContentAuthorsData?.getContentAuthors || [],
        imageSelector,
        screenshotSelector,
        isAddNewsEnabled,
        isAdmin,
        isEditor,
        label: "Add News",
        loading,
        name,
        onAddNews,
        onClearAll,
        onContentAuthorSelect,
        onNameChange,
        onSetReleaseDate,
        releaseDate,
        searchHomeVideo,
        searchNewsSource
    };
};
export default useNewsAddActions;
